import { toFullName } from '@repo/toolkit/to-full-name'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Button from '@repo/ui/button'
import { AddUserIcon, EmailIcon, PlusIcon, UserFourIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME, MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

import { API } from '../../api/apis.config'
import { getPeriods } from '../../api/periods/get-periods'
import { sendEmailToAffectedStaffs } from '../../api/staff-requests/send-email-to-affected-staffs'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../components/elements/nav-bar'
import SendEmailForm from '../../components/pages/candidates/send-email-form'
import { EmailModal } from '../../components/pages/common/email-modal'
import AffectStaff from '../../components/pages/staff-requests/affect-staff'
import { StaffRequestsFilters } from '../../components/pages/staff-requests/staff-requests-filters'
import { Paths } from '../../lib/constants/path'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import { AffectedStaffStatusEnum } from '../../lib/types/enums/affected-staff/affected-staff-status.enum'
import { StaffRequest } from '../../lib/types/models/staff-request/staff-request'
import { StaffRequestFilters } from '../../lib/types/models/staff-request/staff-request-input'
import { modalButtonConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import {
  transformStaffRequestFiltersToUrl,
  transformUrlToStaffRequestFilters
} from '../../lib/utils/filters-mapping/staff-request'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import { staffRequestColumns } from './utils'

const StaffRequestListPage = () => {
  const { navigateWithEvent } = useNavigator()
  const tableRef = useRef<DynamicTableRef<StaffRequest>>(null)
  const [isTableLoading, setIsTableLoading] = useState(false)

  const { data: periods } = useQuery({
    queryKey: ['periods', 'LIGHT'],
    queryFn: () => getPeriods({ version: 'LIGHT' })
  })

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<StaffRequestFilters>({
      // Stays already over are hidden when the page opens, the user can still show them
      initialFilters: { hideFinishedStays: true },
      options: { allowSearchParam: true },
      transformFilterToUrl: transformStaffRequestFiltersToUrl,
      transformUrlToFilter: transformUrlToStaffRequestFilters
    })

  const { selectedData, modalState, toggleModal, closeModel, closeAll } = useModalManager<
    'filters' | 'affect-staff' | 'send-email' | 'send-affection-email' | 'send-deaffection-email',
    StaffRequest[]
  >(['filters', 'affect-staff', 'send-email', 'send-affection-email', 'send-deaffection-email'])

  const sendEmailToAffectedStaffsMutation = useMutation({
    mutationFn: sendEmailToAffectedStaffs,
    onSuccess: () => {
      toast.success('Les emails ont été envoyés avec succès')
      closeModel('send-email')
    },
    onError: showAxiosError
  })

  const emails: string[] = selectedData
    ? [
        ...new Set(
          selectedData.flatMap((staffRequest) =>
            staffRequest.affectedStaffs
              .filter(
                (affectedStaff) => affectedStaff.status !== AffectedStaffStatusEnum.CANCELLED
              )
              .map((affectedStaff) => affectedStaff.staff.user.email)
          )
        )
      ]
    : []

  return (
    <div>
      <UserNavBar title={'Demande des MEs'} icon={UserFourIcon} />
      <div className={'h-4'} />
      <DynamicDataTable<StaffRequest, StaffRequestFilters>
        ref={tableRef}
        onLoadingChange={setIsTableLoading}
        title={'Toutes les demandes'}
        columns={[
          ...staffRequestColumns(periods?.data ?? []),
          {
            accessorKey: 'id',
            header: 'Affecter un encadrant',
            cell: ({ row }) => (
              <Button
                variant="secondary"
                className="px-2 h-9"
                icon={PlusIcon}
                onClick={(e) => {
                  e.stopPropagation()
                  toggleModal('affect-staff', [row.original])
                }}
              />
            )
          }
        ]}
        selectable
        disableRow={(row) => row.affectedStaffs.length === 0}
        allowSearchParam
        endpoint={API.staffRequest.getAll.url}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        onFiltersClick={() => toggleModal('filters')}
        permissions={{ delete: false }}
        onRowClick={(staffRequest, event) =>
          navigateWithEvent(
            Paths.STAFF_REQUEST_DETAIL_PAGE.replace(':id', staffRequest.id.toString()),
            event
          )
        }
        actions={[
          {
            type: 'multiple',
            icon: EmailIcon,
            variant: 'ghost',
            label: 'Email groupé',
            fn: (data) => {
              toggleModal('send-email', data)
            }
          }
        ]}
      />

      {/* Filters Modal */}
      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <StaffRequestsFilters filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>

      {selectedData && (
        <Modal
          isOpen={modalState['affect-staff']}
          title="Affecter un encadrant"
          size="large"
          hideActions
          icon={AddUserIcon}
          onCancel={() => toggleModal('affect-staff')}
        >
          <AffectStaff
            className={MODAL_CONTENT_CLASSNAME}
            staffRequestId={selectedData[0].id}
            toggleModal={() => toggleModal('affect-staff')}
            onSave={() => {
              tableRef.current?.refetch()

              // if the center is locked, we toggle the send email modal to notify the admins, otherwise we just close the affect staff modal
              const isCenterLocked = selectedData[0].centers?.[0]?.locked
              if (isCenterLocked) toggleModal('send-affection-email')

              closeModel('affect-staff')
            }}
          />
        </Modal>
      )}

      <EmailModal
        emailApiEndpoint={API.affectedStaffs.sendAffectedStaffAffectionNotification.url}
        label="Vous avez affecté un encadrant à un centre verrouillé. Souhaitez-vous envoyer une notification ?"
        isOpen={modalState['send-affection-email']}
        closeModal={() => closeAll()}
      />

      {selectedData && (
        <Modal
          isOpen={modalState['send-email']}
          title={'Envoyer un email'}
          icon={EmailIcon}
          size="medium"
          hideActions
          onCancel={() => closeModel('send-email')}
        >
          <SendEmailForm
            className={MODAL_BODY_CLASSNAME}
            defaultValues={{
              emails,
              subject: '',
              body: ''
            }}
            labels={[
              ...new Map(
                selectedData.flatMap((staffRequest) =>
                  staffRequest.affectedStaffs
                    .filter(
                      (affectedStaff) => affectedStaff.status !== AffectedStaffStatusEnum.CANCELLED
                    )
                    .map((affectedStaff) => {
                      const email = affectedStaff.staff.user.email
                      const label = `${toFullName(
                        affectedStaff.staff.firstName,
                        affectedStaff.staff.lastName,
                        { options: { mode: 'lastname-firstname' } }
                      )} - ${email}`
                      return [email, label]
                    })
                )
              ).values()
            ]}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('send-email'),
              isLoading: sendEmailToAffectedStaffsMutation.isPending,
              onConfirm: (values) =>
                sendEmailToAffectedStaffsMutation.mutate({ ...values, emails })
            })}
          />
        </Modal>
      )}
    </div>
  )
}

export default StaffRequestListPage
